import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslate } from "@/hooks/use-translate/useTranslate";

export interface SearcherSelectProps {
  title: string;
  value: string | null;
  onChange: (value: string, title: string) => void;
  options: string[] | { id: number; name: string }[];
}

const SearcherSelect: React.FC<SearcherSelectProps> = ({
  title,
  value,
  onChange,
  options,
}) => {
  const translate = useTranslate();

  const [shownOptions, setShownOptions] = useState(false);

  const changeOptionsState = useCallback(() => {
    const newState = !shownOptions;
    setShownOptions(newState);
  }, [shownOptions, setShownOptions]);

  const selectOption = useCallback(
    (
      event:
        | React.MouseEvent<HTMLButtonElement>
        | React.TouchEvent<HTMLButtonElement>,
    ) => {
      const selected = event.currentTarget.value;
      onChange(selected, title);
      setShownOptions(false);
    },
    [onChange, setShownOptions],
  );

  const deleteData = useCallback(() => {
    onChange("", title);
    setShownOptions(false);
  }, [onChange, setShownOptions]);

  const closeBecouseBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (!event.relatedTarget?.id.includes(`select-${title}`))
        setShownOptions(false);
    },
    [title],
  );

  const dataToShow = useMemo(() => {
    if (!value) return "";
    if (typeof options[0] === "string") return value;

    const selected = (options as { id: number; name: string }[]).find(
      (option) => option.id.toString() === value,
    );
    return selected?.name || "";
  }, [value, options]);

  return (
    <div
      className="w-full single-select font-normal text-[14px] leading-[100%] relative bg-ib-white rounded-[4px]"
      data-testid="select-container"
      onBlur={closeBecouseBlur}
    >
      <div className="select-input-group w-full flex flex-col items-start justify-between">
        <button
          id={title}
          name={title}
          onClick={changeOptionsState}
          className={`searcher-select-input text-center`}
        >
          <span>{dataToShow}</span>
        </button>
      </div>
      {shownOptions ? (
        <div className="searcher-select-options leading-[20px]">
          <button
            id={`select-${title}`}
            value={title}
            className="cursor-default h-7 border-b border-b-ib-gray-lightest w-full px-4 text-[12px] text-ib-gray-main"
            onClick={deleteData}
          >
            {translate(title)}
          </button>
          <div className="h-[calc(100%-28px)] min-h-[calc(100%-28px)] w-full overflow-y-auto">
            {options.map((option, index) => {
              if (typeof option === "string") {
                return (
                  <button
                    id={`select-${title}-${index}`}
                    value={option}
                    className={`h-12 w-full text-left cursor-default flex flex-col justify-center text-ib-gray-darkest text-[14px] hover:bg-ib-red-light px-4${
                      index !== options.length - 1
                        ? " border-b border-b-ib-gray-lightest"
                        : ""
                    }`}
                    key={option}
                    onClick={selectOption}
                  >
                    {option}
                  </button>
                );
              } else {
                return (
                  <button
                    id={`select-${title}-${option.name}`}
                    value={option.id}
                    className={`h-12 w-full text-left cursor-default flex flex-col justify-center text-ib-gray-darkest text-[14px] hover:bg-ib-red-light px-4${
                      index !== options.length - 1
                        ? " border-b border-b-ib-gray-lightest"
                        : ""
                    }`}
                    key={option.id}
                    onClick={selectOption}
                  >
                    {option.name}
                  </button>
                );
              }
            })}
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default memo(SearcherSelect);
